/**
 * Bootstrap/reactstrap can leave `body.modal-open`, orphaned `.modal-backdrop`
 * nodes, and an invisible `.modal.show` after nested or interrupted closes.
 *
 * reactstrap does not put `.modal` / `.modal-backdrop` on `document.body`.
 * It appends a portal host div, then renders both nodes inside that host:
 *
 *   body
 *     #root
 *     div[tabindex="-1"]          // portal host
 *       div
 *         .modal
 *         .modal-backdrop.show    // this is the dim layer
 *
 * Cleanup that only looks at `body > .modal-backdrop` will never find it.
 * Present Screen automation/widget layers live inside `#root .app-scale`
 * and must never be touched.
 */

let pendingCleanupTimer = null;

const isReactstrapPortalHost = (child) => {
  if (!(child instanceof HTMLElement) || child.id === "root") return false;
  if (child.classList.contains("modal") || child.classList.contains("modal-backdrop")) {
    return true;
  }
  return Boolean(child.querySelector(".modal, .modal-backdrop"));
};

const getReactstrapPortalHosts = () => {
  if (typeof document === "undefined") return [];
  return Array.from(document.body.children).filter(isReactstrapPortalHost);
};

const getBootstrapBackdrops = () => {
  if (typeof document === "undefined") return [];
  const found = [];
  getReactstrapPortalHosts().forEach((host) => {
    if (host.classList.contains("modal-backdrop")) found.push(host);
    found.push(...host.querySelectorAll(".modal-backdrop"));
  });
  return [...new Set(found)];
};

const getBootstrapModals = () => {
  if (typeof document === "undefined") return [];
  const found = [];
  getReactstrapPortalHosts().forEach((host) => {
    if (host.classList.contains("modal")) found.push(host);
    found.push(...host.querySelectorAll(".modal"));
  });
  return [...new Set(found)];
};

const getModalOpacity = (modal) => {
  if (typeof window === "undefined" || typeof window.getComputedStyle !== "function") {
    return 1;
  }
  try {
    const opacity = Number.parseFloat(window.getComputedStyle(modal).opacity);
    return Number.isFinite(opacity) ? opacity : 1;
  } catch {
    return 1;
  }
};

const isRandomizerFullBootstrapModal = (modal) => Boolean(
  modal instanceof Element
  && (
    modal.querySelector(".randomizer-edit-modal")
    || modal.classList.contains("randomizer-edit-modal")
    || modal.getAttribute("data-testid") === "present-widget-randomizer-full"
  )
);

const isVisiblyOpenBootstrapModal = (modal) => {
  if (!(modal instanceof Element) || !modal.classList.contains("show")) return false;
  // React still owns this parked portal. Fade-in opacity 0 and leftover
  // teardown styles must not count as "no modal open" or cleanup hides it.
  if (isRandomizerFullBootstrapModal(modal)) {
    if (modal.style.display === "none") return false;
    try {
      return window.getComputedStyle(modal).display !== "none";
    } catch {
      return true;
    }
  }
  if (modal.getAttribute("aria-hidden") === "true") return false;
  if (modal.style.pointerEvents === "none") return false;
  if (typeof window === "undefined" || typeof window.getComputedStyle !== "function") {
    return true;
  }
  try {
    const style = window.getComputedStyle(modal);
    if (style.display === "none" || style.visibility === "hidden") return false;
    if (getModalOpacity(modal) < 0.05) return false;
  } catch {
    return true;
  }
  return true;
};

const isActiveBootstrapBackdrop = (backdrop) => {
  if (!(backdrop instanceof Element)) return false;
  if (!backdrop.classList.contains("show")) return false;
  if (backdrop.style.display === "none") return false;
  if (backdrop.style.pointerEvents === "none") return false;
  if (backdrop.getAttribute("aria-hidden") === "true") return false;
  return true;
};

const getActiveBootstrapBackdrops = () => getBootstrapBackdrops().filter(isActiveBootstrapBackdrop);

const getVisibleOpenBootstrapModals = () => getBootstrapModals().filter(isVisiblyOpenBootstrapModal);

export const hasOpenBootstrapModal = () => getVisibleOpenBootstrapModals().length > 0;

export const hasStaleBootstrapModalArtifacts = () => {
  if (typeof document === "undefined") {
    return false;
  }
  const openModals = getVisibleOpenBootstrapModals();
  const activeBackdrops = getActiveBootstrapBackdrops();
  if (openModals.length === 0) {
    return document.body.classList.contains("modal-open")
      || activeBackdrops.length > 0
      || getBootstrapModals().some((modal) => modal.classList.contains("show"));
  }
  return activeBackdrops.length > openModals.length;
};

const restoreBodyScrollLock = () => {
  document.body.classList.remove("modal-open");
  if (document.body.style.overflow === "hidden") {
    document.body.style.overflow = "";
  }
  if (document.body.style.paddingRight) {
    document.body.style.paddingRight = "";
  }
};

const disableBackdropLayer = (backdrop) => {
  backdrop.classList.remove("show");
  backdrop.setAttribute("aria-hidden", "true");
  backdrop.style.display = "none";
  backdrop.style.opacity = "0";
  backdrop.style.pointerEvents = "none";
};

const disableSiblingBackdrops = (modal) => {
  const wrap = modal.parentElement;
  if (!wrap || wrap === document.body) return;
  Array.from(wrap.children).forEach((child) => {
    if (child instanceof Element && child.classList.contains("modal-backdrop")) {
      disableBackdropLayer(child);
    }
  });
};

const disableModalLayer = (modal) => {
  modal.classList.remove("show");
  modal.setAttribute("aria-hidden", "true");
  modal.style.display = "none";
  modal.style.pointerEvents = "none";
  disableSiblingBackdrops(modal);
};

const clearLayerTeardownStyles = (element) => {
  element.style.removeProperty("display");
  element.style.removeProperty("opacity");
  element.style.removeProperty("pointer-events");
  if (element.getAttribute("aria-hidden") === "true") {
    element.removeAttribute("aria-hidden");
  }
};

const restoreSiblingBackdrops = (modal) => {
  const wrap = modal.parentElement;
  if (!wrap || wrap === document.body) return;
  Array.from(wrap.children).forEach((child) => {
    if (child instanceof Element && child.classList.contains("modal-backdrop")) {
      // Clear cleanup inline styles so Reactstrap can show this portal's dim.
      // Do not add `.show` — that would revive leftover dims in other portals.
      clearLayerTeardownStyles(child);
    }
  });
};

export const restoreDisabledBootstrapModalLayers = ({
  dialogSelector = ".agenda-box-editor-layer",
} = {}) => {
  if (typeof document === "undefined") return;

  document.querySelectorAll(dialogSelector).forEach((dialog) => {
    const modal = dialog.closest(".modal") || (dialog.classList.contains("modal") ? dialog : null);
    if (!modal) return;
    modal.style.display = "block";
    modal.style.removeProperty("pointer-events");
    modal.style.removeProperty("opacity");
    if (modal.getAttribute("aria-hidden") === "true") {
      modal.removeAttribute("aria-hidden");
    }
    const host = getReactstrapPortalHosts().find((candidate) => candidate.contains(modal));
    if (host && host !== modal) {
      host.style.removeProperty("display");
      host.style.removeProperty("pointer-events");
      host.removeAttribute("aria-hidden");
    }
    restoreSiblingBackdrops(modal);
  });
};

const modalLooksDisplayed = (modal) => {
  const inlineDisplay = modal.style.display;
  return inlineDisplay === "block" || inlineDisplay === "flex";
};

const hideStuckClosingModals = () => {
  getBootstrapModals().forEach((modal) => {
    if (isRandomizerFullBootstrapModal(modal)) return;
    if (isVisiblyOpenBootstrapModal(modal)) return;

    // Invisible leftover `.show` overlays still intercept clicks.
    if (modal.classList.contains("show")) {
      disableModalLayer(modal);
      return;
    }

    // Parked `unmountOnClose={false}` dialogs keep `.fade` without `.show`.
    // Leave already-hidden ones alone so React can reopen the same portal.
    // Only disable a fading layer that is still display:block and can steal clicks.
    const isFadingOut = modal.classList.contains("fade");
    if (isFadingOut && modalLooksDisplayed(modal)) {
      disableModalLayer(modal);
    }
  });
};

const hideOrphanedBackdrops = (openModalCount) => {
  const backdrops = getBootstrapBackdrops();
  if (openModalCount === 0) {
    backdrops.filter(isActiveBootstrapBackdrop).forEach(disableBackdropLayer);
    return;
  }
  const activeBackdrops = backdrops.filter(isActiveBootstrapBackdrop);
  if (activeBackdrops.length > openModalCount) {
    activeBackdrops.slice(openModalCount).forEach(disableBackdropLayer);
  }
};

export const disableOrphanedBootstrapModalLayers = ({
  dialogSelector = ".agenda-box-editor-layer",
} = {}) => {
  if (typeof document === "undefined") return;

  document.querySelectorAll(dialogSelector).forEach((dialog) => {
    const modal = dialog.closest(".modal") || (dialog.classList.contains("modal") ? dialog : null);
    if (modal) disableModalLayer(modal);
  });

  hideStuckClosingModals();

  const remainingOpen = getVisibleOpenBootstrapModals();
  hideOrphanedBackdrops(remainingOpen.length);
  if (remainingOpen.length === 0) {
    restoreBodyScrollLock();
  }
};

const runCleanupAttempt = (attempt, maxAttempts, retryMs) => {
  pendingCleanupTimer = null;
  const openModals = getVisibleOpenBootstrapModals();

  if (openModals.length === 0) {
    restoreBodyScrollLock();
    hideOrphanedBackdrops(0);
    hideStuckClosingModals();
    return;
  }

  hideOrphanedBackdrops(openModals.length);

  if (attempt + 1 >= maxAttempts) {
    hideStuckClosingModals();
    return;
  }
  pendingCleanupTimer = window.setTimeout(
    () => runCleanupAttempt(attempt + 1, maxAttempts, retryMs),
    retryMs
  );
};

export const cancelStaleModalArtifactCleanup = () => {
  if (typeof window === "undefined" || pendingCleanupTimer == null) {
    return;
  }
  window.clearTimeout(pendingCleanupTimer);
  pendingCleanupTimer = null;
};

export const prepareBootstrapModalForOpen = ({
  dialogSelector = ".agenda-box-editor-layer",
} = {}) => {
  cancelStaleModalArtifactCleanup();
  restoreDisabledBootstrapModalLayers({ dialogSelector });
};

export const cleanupStaleModalArtifacts = ({
  delayMs = 350,
  maxAttempts = 8,
  retryMs = 100,
} = {}) => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }

  cancelStaleModalArtifactCleanup();

  pendingCleanupTimer = window.setTimeout(() => {
    runCleanupAttempt(0, maxAttempts, retryMs);
  }, delayMs);
};

export const resetStaleModalArtifactCleanupForTests = () => {
  cancelStaleModalArtifactCleanup();
};
